import React, { useState, useEffect } from 'react';
import FullPageLoad from 'components/ui/FullPageLoad';
import BaseWrapper from 'components/layout/BaseWrapper';
import { ButtonFilled } from 'components/ui/Button';
import TournamentDetails from 'components/CreatingTournament/TournamentDetails';
import TournamentLoad from 'components/CreatingTournament/TournamentLoad';
import TournamentCreating from 'components/CreatingTournament/TournamentCreating';
import { createTournament } from 'api/tournamentService';
import type { CreateTournamentPayload } from 'api/tournamentService';
import { TournamentResponse, TournamentResponseDataBase } from 'types/tournament';
import { sanitizePaymentError } from 'utils/payment/errorSanitizer';
import { useNavigate } from 'react-router-dom';
import * as routes from 'constants/routes';
import { getTranslations } from './i18n';

import {
  Wrapper,
  Article,
  Title,
  SubTitle,
  LoadAnime,
  SubTitleLarge,
  Row,
} from './styled';

type CreateTournamentFormPayload = CreateTournamentPayload & {
  stake: number;
};

// TournamentResponse imported from 'types/tournament' (Task 41 - type unification)

type CreatingTournamentData = {
  id: number | string;
  attributes: TournamentResponseDataBase;
};

const CreatingTournament = () => {
  const navigate = useNavigate();
  const t = getTranslations();

  const [isloadPage, setLoadPage] = useState<Promise<boolean | void> | boolean>(true);
  const [a11yAnnouncement, setA11yAnnouncement] = useState<string>('');
  const [isOpenedViewLoad, setIsOpenedViewLoad] = useState(false);
  const [isOpenedDetails, setIsOpenedDetails] = useState(false);
  const [isOpenedShareQR, setIsOpenedShareQR] = useState(false);
  const [isOpenedCreating, setIsOpenedCreating] = useState(false);
  const [tournamentData, setTournamentData] = useState<CreatingTournamentData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const pendingTournamentData = localStorage.getItem('pendingTournamentData');
    if (pendingTournamentData) {
      const createPendingTournament = async () => {
        try {
          // Create the tournament with the stored data
          const tournamentParams = JSON.parse(pendingTournamentData);
          // stake_currency must be set on paid tournaments; if absent in stored data, omit it
          // and let backend validation surface the error rather than silently defaulting to POL
          console.log('Creating pending tournament:', tournamentParams);
          const response = await createTournament(tournamentParams);
          console.log('Tournament created:', response.data);

          // Clean up stored data
          localStorage.removeItem('pendingTournamentData');

          // Update state to show tournament details (normalise attributes/new flat shape)
          const raw: any = response.data;
          const attributes = raw?.attributes ?? raw;
          const tournamentDataWithType: CreatingTournamentData = {
            id: raw.id,
            attributes: {
              ...attributes,
              tournament_type: tournamentParams.tournament_type,
            },
          };

          setIsOpenedViewLoad(false);
          // Navigate to game (free) or wallet entry (paid)
          const tid = raw.id.toString();
          if (tournamentParams.stake > 0) {
            navigate(routes.SET_WALLET_ADDRESS.replace(':id', tid), {
              state: { tournamentId: tid, hash: attributes.hash },
            });
          } else {
            navigate(routes.GAME.replace(':id', tid));
          }
        } catch (err) {
          console.error('Failed to create pending tournament:', err);
          // setError(err.message || 'Failed to create tournament');
          setIsOpenedViewLoad(false);
        }
      };

      setIsOpenedViewLoad(true);
      createPendingTournament();
    }
  }, [navigate]);

  useEffect(() => {
    const timeout = setTimeout(() => setLoadPage(false), 1000)
    return () => clearTimeout(timeout);
  }, []);

  const handleTournamentCreate = async (data: CreateTournamentFormPayload): Promise<TournamentResponse> => {
    setIsOpenedViewLoad(true);
    setError(null);
    setA11yAnnouncement('');

    try {
      // Extract stake from form data
      const { stake, ...tournamentPayload } = data;

      // Backend contract: stake + stake_currency (ticker ID).
      // We always forward stake_currency now that Chain is a first-class
      // form decision, even for Free tournaments. Backend accepts it for
      // stake=0 and uses it to record the chosen chain.
      const stakeCurrencyId = (data as { stake_currency?: number }).stake_currency;
      const response = await createTournament({
        ...tournamentPayload,
        stake,
        ...(stakeCurrencyId != null ? { stake_currency: stakeCurrencyId } : {}),
      });
      console.log('Tournament created:', response.data);
      const raw: any = response.data;
      const attributes = raw?.attributes ?? raw;
      const tournamentId = raw.id.toString();

      const tournamentDataWithType: CreatingTournamentData = {
        id: raw.id,
        attributes: {
          ...attributes,
          tournament_type: data.tournament_type,
        },
      };

      setTournamentData(tournamentDataWithType);

      // Stake = 0: free tournament — navigate to game view
      if (stake === 0) {
        setIsOpenedViewLoad(false);
        navigate(routes.GAME.replace(':id', tournamentId));
        return response;
      }

      // Stake > 0: redirect to wallet address entry flow
      setIsOpenedViewLoad(false);
      navigate(routes.SET_WALLET_ADDRESS.replace(':id', tournamentId), {
        state: {
          tournamentId,
          hash: attributes.hash,
        },
      });
      return response;
    } catch (err) {
      // Tournament creation failed - sanitize error (Task 33.4)
      const sanitized = sanitizePaymentError(err as Error);
      console.error('[TournamentError]', {
        code: (err as Error & { code?: string }).code || 'UNKNOWN',
        details: sanitized.details,
      });
      setError(sanitized.userMessage);
      setIsOpenedViewLoad(false);
      throw err;
    }
  };

  if (isloadPage) {
    return <FullPageLoad/>;
  }

  return (
    <BaseWrapper compact>
      <Wrapper>
        {!isOpenedCreating && !isOpenedViewLoad && !isOpenedDetails && (
          <Article className="centerItem">
            <TournamentCreating
              onCreateTournament={handleTournamentCreate}
              setIsOpenedViewLoad={setIsOpenedViewLoad}
              errorInfo={error}
            />
          </Article>
        )}

        {isOpenedViewLoad && (
          <TournamentLoad>
            <Title>Your game is being created</Title>
            <LoadAnime/>
            <SubTitle>Please wait...</SubTitle>
          </TournamentLoad>
        )}

        {isOpenedDetails && tournamentData && (
          <>
            <TournamentDetails tournamentData={tournamentData} />
          </>
        )}

        {isOpenedShareQR && (
          <TournamentLoad>
            <Row>
              <SubTitleLarge>Player: <span>CryptoJohn123</span></SubTitleLarge>
            </Row>
          </TournamentLoad>
        )}

        {error && (
          <div 
            role="alert" 
            aria-live="assertive"
            style={{ color: 'red', textAlign: 'center', marginTop: '1rem' }}
          >
            {error}
          </div>
        )}

        {/* Screen reader announcements */}
        <div 
          role="status" 
          aria-live="polite" 
          aria-atomic="true"
          className="sr-only"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
        >
          {a11yAnnouncement}
        </div>
      </Wrapper>
    </BaseWrapper>
  );
};

export default CreatingTournament;
